import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { OrgProvider, useOrg } from "@/context/OrgContext";
import Splash from "@/components/Splash";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Onboarding from "@/pages/Onboarding";
import BizDashboard from "@/pages/BizDashboard";
import Businesses from "@/pages/Businesses";
import Inbox from "@/pages/Inbox";
import Automations from "@/pages/Automations";
import EntityList from "@/pages/EntityList";
import { DealPage } from "@/pages/DealPage";
import Team from "@/pages/Team";
import Billing from "@/pages/Billing";
import PlatformConsole from "@/pages/PlatformConsole";
import Chat from "@/pages/Chat";
import Memory from "@/pages/Memory";
import Files from "@/pages/Files";
import Settings from "@/pages/Settings";
import Search from "@/pages/Search";
import WhatWeKnow from "@/pages/WhatWeKnow";
import Decisions from "@/pages/Decisions";
import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import BillingSuccess from "@/pages/BillingSuccess";
import LegalWorkspace from "@/pages/LegalWorkspace";

function Protected({ children }) {
  const { user, ready } = useAuth();
  const loc = useLocation();
  if (!ready) return <Splash />;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <Splash />;
  if (user) return <Navigate to="/app/dashboard" replace />;
  return children;
}

function OnboardGate({ children }) {
  const { current, loading } = useOrg();
  const loc = useLocation();
  if (loading) return <Splash />;
  if (current && !current.org.onboarded && loc.pathname !== "/app/onboarding") {
    return <Navigate to="/app/onboarding" replace />;
  }
  return children;
}

function Shell() {
  const [booting, setBooting] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setBooting(false), 2000);
    return () => clearTimeout(t);
  }, []);
  return (
    <>
      <AnimatePresence>{booting && <Splash />}</AnimatePresence>
      <Routes>
        <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
        <Route path="/reset-password" element={<Login />} />
        <Route path="/onboarding" element={<Navigate to="/app/onboarding" replace />} />
        <Route path="/app/onboarding" element={<Protected><Onboarding /></Protected>} />
        <Route path="/app" element={<Protected><OnboardGate><Layout /></OnboardGate></Protected>}>
          <Route index element={<Navigate to="/app/dashboard" replace />} />
          <Route path="dashboard" element={<BizDashboard />} />
          <Route path="businesses" element={<Businesses />} />
          <Route path="inbox" element={<Inbox />} />
          <Route path="automations" element={<Automations />} />
          <Route path="crm/:entity" element={<EntityList />} />
          <Route path="quotes" element={<DealPage kind="quotes" />} />
          <Route path="jobs" element={<DealPage kind="jobs" />} />
          <Route path="invoices" element={<DealPage kind="invoices" />} />
          <Route path="legal" element={<LegalWorkspace />} />
          <Route path="team" element={<Team />} />
          <Route path="billing" element={<Billing />} />
          <Route path="platform" element={<PlatformConsole />} />
          <Route path="files" element={<Files />} />
          <Route path="memory" element={<Memory />} />
          <Route path="search" element={<Search />} />
          <Route path="profile" element={<WhatWeKnow />} />
          <Route path="decisions" element={<Decisions />} />
          <Route path="settings" element={<Settings />} />
          <Route path="chat/:id" element={<Chat />} />
        </Route>
        <Route path="/" element={<Landing />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <OrgProvider>
            <Shell />
            <Toaster theme="dark" position="top-center" toastOptions={{ style: { background: "#0A0A0A", border: "1px solid #1f1f1f", color: "#fff" } }} />
          </OrgProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
